import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Sun, Moon, Monitor } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Footer = () => {
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();
  
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m the Connectify AI Assistant. Get help with finding the right eSIM plan, or get help with your eSIM!' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.success) {
        setSessionId(response.data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.response || 'I had trouble understanding. Try asking about a destination!' }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I\'m having connection issues. Check out our destinations - plans start at $0.31!' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <footer className="bg-black text-white py-12 dark:border-t dark:border-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            {/* Brand */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6.5" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span className="font-extrabold text-lg" style={{ fontFamily: "'Montserrat', sans-serif" }}>Connectify</span>
            </div>

            {/* Links */}
            <div className="flex flex-wrap gap-8 text-sm">
              <Link to="/destinations" className="text-gray-400 dark:text-neutral-500 hover:text-white transition-colors">All Destinations</Link>
              <Link to="/blog" className="text-gray-400 dark:text-neutral-500 hover:text-white transition-colors" data-testid="footer-blog-link">Blog</Link>
              <Link to="/login" className="text-gray-400 dark:text-neutral-500 hover:text-white transition-colors">Sign In</Link>
            </div>
          </div>

          {/* Theme switcher */}
          <div className="mt-8 flex items-center justify-center gap-2" data-testid="footer-theme-switcher">
            <span className="text-xs text-gray-400 mr-2">Theme:</span>
            {[
              { value: 'light', label: 'Light', Icon: Sun },
              { value: 'dark', label: 'Dark', Icon: Moon },
              { value: 'auto', label: 'Auto', Icon: Monitor },
            ].map(({ value, label, Icon }) => {
              const active = mode === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setMode(value)}
                  aria-pressed={active}
                  data-testid={`footer-theme-${value}`}
                  className={
                    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ` +
                    (active
                      ? 'bg-white text-black border-white'
                      : 'bg-transparent text-gray-300 border-white/30 hover:bg-white/10 hover:text-white')
                  }
                >
                  <Icon size={14} />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Copyright */}
          <div className="border-t border-white/10 mt-8 pt-8 flex justify-center">
            <p className="text-xs text-gray-500 dark:text-neutral-500">© 2025 Connectify eSIM. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* AI Chatbot FAB */}
      <button
        onClick={() => setIsChatOpen(true)}
        className={`fixed bottom-6 right-6 w-14 h-14 bg-black text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-40 ${isChatOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="chat-fab"
      >
        <MessageCircle size={24} />
      </button>

      {/* Chat Modal */}
      {isChatOpen && (
        <div className="fixed bottom-6 right-6 w-[380px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-white dark:bg-neutral-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-neutral-800 flex flex-col z-50 animate-scale-in overflow-hidden" data-testid="chat-modal">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-950">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                <MessageCircle className="text-white" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-neutral-100 text-sm">Connectify AI Assistant</h3>
                <p className="text-xs text-gray-500 dark:text-neutral-500">AI-powered recommendations</p>
              </div>
            </div>
            <button onClick={() => setIsChatOpen(false)} className="text-gray-400 dark:text-neutral-500 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-black text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-neutral-800 px-4 py-3 rounded-2xl rounded-bl-md">
                  <Loader2 className="animate-spin text-gray-500 dark:text-neutral-500" size={20} />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-100 dark:border-neutral-800">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask about a destination..."
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 text-sm focus:outline-none focus:border-black"
                data-testid="chat-input"
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="w-12 h-12 bg-black text-white rounded-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="chat-send"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .animate-scale-in { animation: scaleIn 0.2s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </>
  );
};
